import { createAlova } from 'alova'
import adapterFetch from 'alova/fetch'
import { xhrRequestAdapter } from '@alova/adapter-xhr'
import VueHook from 'alova/vue'
// import { useMessage } from 'naive-ui'
import Qs from 'qs'
import { log } from './log'

const defaultRequestInterceptor = (method) => {
  // method.config.headers['Content-Type'] = 'application/x-www-form-urlencoded'
  method.config.headers['Content-Type'] = 'application/json'
  const accept = method?.url?.endsWith('xlsx')
    ? `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`
    : method?.url?.endsWith('csv')
      ? `text/csv`
      : 'application/json, text/plain, */*'
  method.config.headers['Accept'] = accept
  method.config.headers['Accept-Encoding'] = 'gzip, deflate, br'
  const params = method.params
  if (params) {
    method.params = Qs.stringify(params, {
      arrayFormat: 'repeat',
    })
  }
}

const defaultAlovaOptions = {
  baseURL: import.meta.env.VUE_APP_API_URL,
  statesHook: VueHook,
  requestAdapter: adapterFetch(),
  beforeRequest: defaultRequestInterceptor,
  responded: (response) => response.json(),
}

function newAlova(opts, tokenFnOrValue) {
  const finalOpts = { ...defaultAlovaOptions, ...opts }
  if (tokenFnOrValue && !opts.beforeRequest) {
    if (typeof tokenFnOrValue === 'function') {
      finalOpts.beforeRequest = (method) => {
        const token = tokenFnOrValue()
        defaultRequestInterceptor(method)
        method.config.headers.sessiontoken = token
      }
    } else {
      finalOpts.beforeRequest = (method) => {
        const token = tokenFnOrValue
        defaultRequestInterceptor(method)
        method.config.headers.sessiontoken = token
      }
    }
  }
  return createAlova(finalOpts)
}

export const createDerivedTokenSendingAndJsonExpectingAlova = (tokenFn, expiredCallback, syncWorker) => {
  // const message = useMessage()

  const opts = {
    responded: {
      onSuccess: async (response, method) => {
        log('JSON Alova Response status', response.status)
        if (response.status === 401) {
          if (expiredCallback != null) expiredCallback()
        } else if (response.status >= 400) {
          throw new Error(response.statusText)
        } else {
          let data = {}
          try {
            const contentType = response.headers.get('Content-Type')
            if (contentType && contentType.includes('application/json')) {
              data = await response.json()
            } else {
              log('Response is not JSON, returning empty JSON object.')
            }
          } catch (error) {
            log('Response is not valid JSON. Returning empty JSON object.', error)
          }
          if (method.type === 'PUT') {
            const formatObjectStoreName = (url) =>
              url
                .substring(1)
                .split('-')
                .map((word) => word[0]?.toUpperCase() + word.slice(1).toLowerCase())
                .join(' ')
            const objectStoreName = formatObjectStoreName(method.url)
            syncWorker.postMessage({
              type: 'sync',
              uri: `sync${method.url}`,
              objectStoreName,
            })
            log(
              'Initiating sync to syncWorker as new record is created for uri',
              method.url,
              'Response',
              data,
              'Dexie store name',
              objectStoreName
            )
          }
          return data
        }
      },
      onError: (err) => log(err.message, err),
    },
  }
  return newAlova(opts, tokenFn)
}

export const createDerivedTokenFormSendingAndJsonExpectingAlova = (tokenFn, expiredCallback) => {
  // const message = useMessage()

  const opts = {
    responded: {
      onSuccess: async (response) => {
        log('Form Alova Response status', response.status)
        if (response.status === 401) {
          if (expiredCallback != null) expiredCallback()
        } else if (response.status >= 400) {
          throw new Error(response.statusText)
        } else return await response.json()
      },
      onError: (err) => log(err.message, err),
    },
  }
  opts.beforeRequest = (method) => {
    defaultRequestInterceptor(method)
    method.config.headers.sessiontoken = tokenFn()
    method.config.headers['Content-Type'] = 'application/x-www-form-urlencoded'
    const data = method.data
    if (data) {
      method.data = Qs.stringify(data, {
        arrayFormat: 'repeat',
      })
    }
  }
  return newAlova(opts, tokenFn)
}

export const createDerivedTokenSendingAndBlobExpectingAlova = (tokenFn) => {
  // const message = useMessage()

  const opts = {
    responded: {
      onSuccess: async (response) => {
        const fileName = response?.headers['content-disposition']
          ? response.headers['content-disposition'].substring(20)
          : `test.xlsx`
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
      },
      onError: (err) => log(err.message, err),
    },
    requestAdapter: xhrRequestAdapter(),
  }
  return newAlova(opts, tokenFn)
}

export const createGivenTokenSendingAndJsonExpectingAlova = (token, tokenInvalidCallback, errorCallback) => {
  const opts = {
    responded: {
      onSuccess: async (response) => {
        if (response.status === 401) tokenInvalidCallback()
        else if (response.status >= 400) throw new Error(response.statusText)
        else return await response.json()
      },
      onError: (err) => errorCallback(err.message),
    },
  }
  return newAlova(opts, token)
}
